import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, X, Rows4, Users, Eye, OctagonAlert, Repeat } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getMuraRecordsByEmail, getAllMuraRecords, acceptMuraRecord, rejectMuraRecord, relabelMuraRecord, getRelabelingRecordsByEmail } from '../../services/apiService';
import { decodedToken } from '../../utils/auth';
import Swal from 'sweetalert2';
import { useGlobalState } from '../../utils/GlobalState';

const MURA_TYPE_DATA = {
  pressing_light_leakage: { id: '0', color: '#FF4500' },
  dark_state_mura: { id: '1', color: '#4682B4' },
  clouding: { id: '2', color: '#9932CC' },
  wavy_mura: { id: '3', color: '#FFD700' },
  lamp_shades: { id: '4', color: '#32CD32' },
  light_bract: { id: '5', color: '#FF6347' },
  grid_mura: { id: '6', color: '#708090' },
  corner_shadow: { id: '7', color: '#FFA500' },
  bright_side_around: { id: '8', color: '#000080' },
  dark_spot: { id: '9', color: '#A52A2A' },
  bright_spot: { id: '10', color: '#9370DB' },
  horizontal_band: { id: '11', color: '#3CB371' },
  vertical_band: { id: '12', color: '#00BFFF' },
  horizontal_line: { id: '13', color: '#FF69B4' },
  vertical_line: { id: '14', color: '#A9A9A9' },
};

const ProductInfoInput = () => {
  const navigate = useNavigate();
  const previewCanvasRef = useRef(null);
  const { state, updateState } = useGlobalState();
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [customRatio, setCustomRatio] = useState('');
  const [customRatios, setCustomRatios] = useState([]);
  const [productType, setProductType] = useState('TV');
  const [brand, setBrand] = useState('');
  const [modelName, setModelName] = useState('');
  const [hasBezel, setHasBezel] = useState(null);
  const [leakageRatio, setLeakageRatio] = useState(null);
  const [dimensions, setDimensions] = useState({
    width: { input: '1920', whole: 1920, decimal: '0' },
    height: { input: '1200', whole: 1200, decimal: '0' },
  });
  const [isLoaded, setIsLoaded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [products, setProducts] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showRelabelingPreview, setShowRelabelingPreview] = useState(false);
  const [relabelingProducts, setRelabelingProducts] = useState([]);
  const [relabelingCount, setRelabelingCount] = useState(0);
  const [showUploadDetails, setShowUploadDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRelabelingLoading, setIsRelabelingLoading] = useState(false);
  const [previewType, setPreviewType] = useState('');
  const [error, setError] = useState(null);
  const [totalUploads, setTotalUploads] = useState(0);
  const [prevTotalUploads, setPrevTotalUploads] = useState(0);
  const [sliderBg, setSliderBg] = useState('linear-gradient(to right, #d1d5db 0%, #d1d5db 100%)');
  const [myUploads, setMyUploads] = useState(0);
  const [myAccepted, setMyAccepted] = useState(0);
  const [totalAccepted, setTotalAccepted] = useState(0);
  const [totalPending, setTotalPending] = useState(0);
  const [submissionBlocked, setSubmissionBlocked] = useState(false);
  const [muraTypeCounts, setMuraTypeCounts] = useState({});
  const [screenType, setScreenType] = useState(null);
  const [selectedInch, setSelectedInch] = useState('');
  const [showWarnings, setShowWarnings] = useState(false);
  const isInitialFetchDone = useRef(false); 
 
const isInitialUploadCountFetched = useRef(false);
  const decoded = decodedToken();
  const email = decoded?.email || '';
  const companyInfo = decoded?.companyInfo || 'Unknown';

  const maxUploads = 10000;

  const predefinedRatios = {
    TV: [1.6, 1.8],
    PD: [1.6, 1.8],
    MNT: [1.0],
  };

  const defaultState = {
    productType: 'TV',
    modelName: '',
    hasBezel: null,
    leakageRatio: null,
    dimensions: {
      width: { input: '', whole: 0, decimal: '0' },
      height: { input: '', whole: 0, decimal: '0' },
    },
    customRatios: [],
    customRatio: '',
    screenType: null,
    inchSize: '',
  };


  const getDefectInfo = (product) => {
    const manualDefects = product?.MuraRecording?.muraLabeling?.defects || [];
    const hasManualDefects = manualDefects.length > 0 &&
      manualDefects.some(defect => (defect.muraType || '').toLowerCase() !== 'other');

    if (hasManualDefects) {
      return manualDefects.filter(defect => (defect.muraType || '').toLowerCase() !== 'other');
    }

    const predictions = product?.issueDetection?.predictions || [];
    return predictions
      .filter(pred => (pred.issueType || '').toLowerCase() !== 'other')
      .map(pred => ({
        muraType: pred.issueType,
        muraLabId: pred.issueId,
        muraLabelIssue: {
          muraRootCause: pred.rootCauseType,
          muraRootIssue: []
        }
      }));
  };

  const computeNormalizedDefects = (product, imageDimensions) => {
    const { width: imgW, height: imgH } = imageDimensions;

    const manualDefects = product?.MuraRecording?.muraLabeling?.defects || [];
    const hasManualDefects = manualDefects.length > 0 &&
      manualDefects.some(defect => (defect.muraType || '').toLowerCase() !== 'other');

    if (hasManualDefects) {
      return manualDefects
        .filter(defect => (defect.muraType || '').toLowerCase() !== 'other')
        .map(defect => ({
          ...defect,
          bbox: defect.bbox || []
        }));
    }

    const predictions = product?.issueDetection?.predictions || [];
    if (predictions.length === 0) return [];

    const naturalMax = Math.max(imgW, imgH);
    let targetSize;
    if (naturalMax > 2000) {
      targetSize = 1280;
    } else if (naturalMax > 1000) {
      targetSize = 832;
    } else {
      targetSize = 640;
    }

    const scale = targetSize / naturalMax;
    const new_width = Math.floor(imgW * scale);
    const new_height = Math.floor(imgH * scale);

    if (new_width === 0 || new_height === 0) return [];

    const scale_to_original_w = imgW / new_width;
    const scale_to_original_h = imgH / new_height;

    return predictions
      .filter(pred => (pred.issueType || '').toLowerCase() !== 'other')
      .map(pred => {
        const box = pred.box || [];
        if (box.length !== 4) return null;

        const [x1, y1, x2, y2] = box;
        if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) {
          return null;
        }

        // Convert to original image coordinates
        const orig_x1 = x1 * scale_to_original_w;
        const orig_y1 = y1 * scale_to_original_h;
        const orig_x2 = x2 * scale_to_original_w;
        const orig_y2 = y2 * scale_to_original_h;

        // Convert to normalized center-based coordinates (0-1 range)
        const centerX = (orig_x1 + orig_x2) / 2 / imgW;
        const centerY = (orig_y1 + orig_y2) / 2 / imgH;
        const w = Math.abs(orig_x2 - orig_x1) / imgW;
        const h = Math.abs(orig_y2 - orig_y1) / imgH;

        // Only clamp to ensure values are within 0-1 range
        const clampedCenterX = Math.max(0, Math.min(1, centerX));
        const clampedCenterY = Math.max(0, Math.min(1, centerY));
        const clampedW = Math.max(0, Math.min(1, w));
        const clampedH = Math.max(0, Math.min(1, h));

        return {
          muraType: pred.issueType,
          muraLabId: pred.issueId,
          muraLabelIssue: {
            muraRootCause: pred.rootCauseType,
            muraRootIssue: []
          },
          bbox: [clampedCenterX, clampedCenterY, clampedW, clampedH]
        };
      })
      .filter(Boolean);
  };

  const countTotalDefects = (products) => {
    let total = 0;
    (products || []).forEach((product) => {
      total += getDefectInfo(product).length;
    });
    return total;
  };

  useEffect(() => {
    setModelName(defaultState.modelName);
    setHasBezel(defaultState.hasBezel);
    setLeakageRatio(defaultState.leakageRatio);
    setCustomRatios(defaultState.customRatios);
    setCustomRatio(defaultState.customRatio);
    setScreenType(defaultState.screenType);
    setSelectedInch(defaultState.inchSize);
    setDimensions(defaultState.dimensions);
    setError(null);
  }, [productType]);

  useEffect(() => {
    if (!email || isInitialFetchDone.current) return;

    const fetchRelabelingCount = async () => {
      try {
        isInitialFetchDone.current = true; 
        const response = await getRelabelingRecordsByEmail(email);
        const count = Array.isArray(response.data) ? response.data.length : 0;
        setRelabelingCount(count);
      } catch (error) {
        console.error('Error fetching relabeling count:', error);
        setRelabelingCount(0); 
      }
    };

    fetchRelabelingCount();

    return () => {
      isInitialFetchDone.current = true;
    };
  }, [email]);

  useEffect(() => {
    if (showImagePreview && previewImageUrl && previewCanvasRef.current && previewProduct) {
      const canvas = previewCanvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get 2D context for preview canvas');
        return;
      }

      const img = new Image();
      img.crossOrigin = "Anonymous";

      img.onload = () => {
        const imageDimensions = { width: img.naturalWidth, height: img.naturalHeight };

        if (state.imageDimensions?.width !== img.naturalWidth || state.imageDimensions?.height !== img.naturalHeight) {
          updateState({ imageDimensions });
        }

        let muraData = {};
        try {
          muraData = JSON.parse(localStorage.getItem('muraData') || '{}');
          if (
            muraData.imageDimensions?.width !== img.naturalWidth ||
            muraData.imageDimensions?.height !== img.naturalHeight
          ) {
            muraData.imageDimensions = imageDimensions;
            localStorage.setItem('muraData', JSON.stringify(muraData));
          }
        } catch (e) {
          console.warn('Failed to parse muraData:', e);
          muraData.imageDimensions = imageDimensions;
          localStorage.setItem('muraData', JSON.stringify(muraData));
        }

        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.style.width = `${img.naturalWidth}px`;
        canvas.style.height = `${img.naturalHeight}px`;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);

        const defects = computeNormalizedDefects(previewProduct, imageDimensions);

        if (defects && Array.isArray(defects)) {
          defects.forEach((defect, index) => {
            if (defect.bbox && Array.isArray(defect.bbox) && defect.bbox.length === 4) {
              let [center_x, center_y, width, height] = defect.bbox;

              if (
                !Number.isFinite(center_x) ||
                !Number.isFinite(center_y) ||
                !Number.isFinite(width) ||
                !Number.isFinite(height)
              ) {
                console.warn(`Defect ${index} has invalid bbox values:`, defect.bbox);
                return;
              }
              const pixelCenterX = center_x * img.naturalWidth;
              const pixelCenterY = center_y * img.naturalHeight;
              const pixelWidth = width * img.naturalWidth;
              const pixelHeight = height * img.naturalHeight;
              const x = pixelCenterX - pixelWidth / 2;
              const y = pixelCenterY - pixelHeight / 2;
              const finalX = Math.max(0, Math.min(x, img.naturalWidth - pixelWidth));
              const finalY = Math.max(0, Math.min(y, img.naturalHeight - pixelHeight));
              const finalWidth = Math.min(pixelWidth, img.naturalWidth - finalX);
              const finalHeight = Math.min(pixelHeight, img.naturalHeight - finalY);
              const muraTypeLower = (defect.muraType || 'other').toLowerCase().replace(/\s+/g, '_');
              //const color = MURA_TYPE_DATA[muraTypeLower]?.color || MURA_TYPE_DATA['other'].color;
              const color = (muraTypeLower && MURA_TYPE_DATA[muraTypeLower]?.color) || (MURA_TYPE_DATA?.other?.color) || '#ff0000'; 
              ctx.strokeStyle = color;
              ctx.lineWidth = 4;
              ctx.strokeRect(finalX, finalY, finalWidth, finalHeight);
              if (defect.muraType && defect.muraLabId && defect.muraType.toLowerCase() !== 'other') {
                ctx.fillStyle = color;
                ctx.font = 'bold 20px Arial';
                const label = `ID: ${defect.muraLabId} | ${defect.muraType}`;
                const textMetrics = ctx.measureText(label);
                const textWidth = textMetrics.width;
                const textHeight = 20;
                let labelX = finalX;
                let labelY = finalY - 10;
                if (labelY - textHeight < 0) {
                  labelY = finalY + finalHeight + textHeight + 10;
                }
                if (labelX + textWidth > img.naturalWidth) {
                  labelX = Math.max(0, finalX + finalWidth - textWidth);
                }
                labelX = Math.max(0, Math.min(labelX, img.naturalWidth - textWidth));
                labelY = Math.max(textHeight, Math.min(labelY, img.naturalHeight));

                ctx.fillText(label, labelX, labelY);
              }
            } else {
              console.warn(`Defect ${index} has invalid bbox:`, defect.bbox);
            }
          });
        }
        setImageLoading(false);
      };

      img.onerror = () => {
        console.error('Failed to load image:', previewImageUrl);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to load image for preview. Please check if the image exists or contact support.',
          icon: 'error',
          confirmButtonText: 'Retry',
          showCancelButton: true,
        }).then((result) => {
          if (result.isConfirmed) {
            img.src = `${previewImageUrl}?t=${Date.now()}`;
          } else {
            closeImagePreview();
          }
        });
      };

      img.src = `${previewImageUrl}?t=${Date.now()}`;
      setImageLoading(true);
    }
  }, [showImagePreview, previewImageUrl, previewProduct, state.imageDimensions, updateState]);



  const calculateMuraTypeCounts = (products) => {
    const counts = {};
    (products || []).forEach((product) => {
      const defectInfos = getDefectInfo(product);
      defectInfos.forEach((defect) => {
        const muraType = defect?.muraType || 'unknown';
        const muraLabId = defect?.muraLabId || '0';
        if (muraType.toLowerCase() !== 'other') {
          if (!counts[muraType]) {
            counts[muraType] = { count: 0, ids: [] };
          }
          counts[muraType].count += 1;
          if (!counts[muraType].ids.includes(muraLabId)) {
            counts[muraType].ids.push(muraLabId);
          }
        }
      });
    });
    return counts;
  };

useEffect(() => {
  if (!state.formData) {
    updateState({ formData: [] });
  }
  const decode = decodedToken();
  setIsAdmin(decode?.userType === 'powerAdmin');
  setIsLoaded(true);
}, []);

  useEffect(() => {
  if (isInitialUploadCountFetched.current) return;

  const fetchInitialUploadCount = async () => {
    try {
      isInitialUploadCountFetched.current = true;
      const professedRecords = await getAllMuraRecords();
      const totalDefects = countTotalDefects(professedRecords);
      const totalRecords = Math.min(totalDefects, maxUploads);
      setTotalUploads(totalRecords);
      setSubmissionBlocked(totalRecords >= maxUploads);
    } catch (error) {
      console.error('Error fetching initial upload count:', error);
      Swal.fire('Error!', 'Failed to fetch initial upload count', 'error');
      isInitialUploadCountFetched.current = true; 
    }
  };

  fetchInitialUploadCount();
}, []); 

  useEffect(() => {
    const percentage = (totalUploads / maxUploads) * 100;
    let gradient;
    if (totalUploads > prevTotalUploads) {
      gradient = `linear-gradient(to right, #3b82f6 ${percentage}%, #d1d5db ${percentage}%)`;
    } else if (totalUploads < prevTotalUploads) {
      gradient = `linear-gradient(to right, #d1d5db ${percentage}%, #3b82f6 ${percentage}%)`;
    } else {
      gradient = totalUploads > 0
        ? `linear-gradient(to right, #3b82f6 ${percentage}%, #d1d5db ${percentage}%)`
        : `linear-gradient(to right, #d1d5db 0%, #d1d5db 100%)`;
    }
    setSliderBg(gradient);
    setPrevTotalUploads(totalUploads);
  }, [totalUploads, prevTotalUploads, maxUploads]);

  const resetForm = () => {
    setProductType(defaultState.productType);
    setModelName(defaultState.modelName);
    setHasBezel(defaultState.hasBezel);
    setLeakageRatio(defaultState.leakageRatio);
    setCustomRatios(defaultState.customRatios);
    setCustomRatio(defaultState.customRatio);
    setScreenType(defaultState.screenType);
    setSelectedInch(defaultState.inchSize);
    setDimensions(defaultState.dimensions);
    updateState({ formData: [] });
  };

  const isFormValid = () => {
    if (!modelName || modelName.trim() === '') {
      return false;
    }

    const width = parseFloat(dimensions.width.input);
    const height = parseFloat(dimensions.height.input);
    if (
      !dimensions.width.input ||
      !dimensions.height.input ||
      isNaN(width) ||
      width <= 0 ||
      isNaN(height) ||
      height <= 0
    ) {
      return false;
    }

    if (productType === 'PD') {
      return true;
    }
    if (productType === 'TV' || productType === 'MNT') {
      if (hasBezel === null) {
        return false;
      }
      if (productType === 'MNT' && screenType === null) {
        return false;
      }
      return true;
    }

    return false;
  };

  const handleBack = () => {
    resetForm();
    navigate('/dashboard');
  };

  const handleDimensionChange = (dimension, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      let whole = 0;
      let decimal = '0';
      if (value !== '') {
        const [w, d = '0'] = value.split('.');
        whole = parseInt(w) || 0;
        decimal = d[0] || '0';
      }
      setDimensions((prev) => ({
        ...prev,
        [dimension]: {
          input: value,
          whole,
          decimal,
        },
      }));
    }
  };

  const handleCustomRatioChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setCustomRatio(value);
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue) && parsedValue > 0 && parsedValue <= 10) {
        setLeakageRatio(parsedValue);
      } else {
        setLeakageRatio(null);
      }
    }
  };

  const addCustomRatio = () => {
    const ratio = parseFloat(customRatio);
    if (ratio && !isNaN(ratio) && !customRatios.includes(ratio) && ratio > 0 && ratio <= 10) {
      setCustomRatios([...customRatios, ratio]);
      setLeakageRatio(ratio);
      setCustomRatio('');
    }
  };

  const fetchMyProducts = async () => {
    setIsLoading(true);
    setPreviewType('my');
    setProducts(null);
    setMuraTypeCounts({});
    try {
      if (!email || typeof email !== 'string' || !email.includes('@')) {
        throw new Error('Invalid or missing email address');
      }
      const data = await getMuraRecordsByEmail(email);
      const allProducts = Array.isArray(data?.data) ? data.data : [];
      const acceptedProducts = allProducts.filter((product) => product.status === 'accepted');
      setMyUploads(countTotalDefects(allProducts));
      setMyAccepted(countTotalDefects(acceptedProducts));
      const allRecords = await getAllMuraRecords();
      const totalDefects = countTotalDefects(allRecords);
      const totalRecords = Math.min(totalDefects, maxUploads);
      setTotalUploads(totalRecords);
      setTotalAccepted(countTotalDefects(allRecords.filter((p) => p.status === 'accepted')));
      setTotalPending(countTotalDefects(allRecords.filter((p) => p.status === null || p.status === 'pending')));
      setSubmissionBlocked(totalRecords >= maxUploads);
      setProducts(acceptedProducts);
      setMuraTypeCounts(calculateMuraTypeCounts(allProducts));
      setShowPreview(true);
      if (allProducts.length === 0) {
        Swal.fire({
          title: 'No Uploads',
          text: 'Zero uploads from this mail for issue labeling.',
          icon: 'info',
          confirmButtonText: 'OK',
        });
      } else if (acceptedProducts.length === 0) {
        Swal.fire({
          title: 'No Accepted Issues',
          text: 'No accepted issues found for your account.',
          icon: 'info',
          confirmButtonText: 'OK',
        });
      }
    } catch (error) {
      console.warn('Failed to fetch products:', {
        message: error.message,
        stack: error.stack,
        email,
      });
      Swal.fire({
        title: 'No Uploads',
        text: 'Zero uploads from this account for issue labeling.',
        icon: 'info',
        confirmButtonText: 'OK',
      });
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    setIsLoading(true);
    setPreviewType('all');
    setProducts(null);
    setMuraTypeCounts({});
    try {
      const data = await getAllMuraRecords();
      const totalDefects = countTotalDefects(data);
      const totalRecords = Math.min(totalDefects, maxUploads);
      const pendingProducts = data
        .filter((product) => product.status === null || product.status === 'pending')
        .filter((product) => ['labeling', 'Detection_disagree'].includes(product.dataFromStatus))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setTotalUploads(totalRecords);
      setTotalAccepted(countTotalDefects(data.filter((p) => p.status === 'accepted')));
      setTotalPending(countTotalDefects(pendingProducts));
      setSubmissionBlocked(totalRecords >= maxUploads);
      setProducts(pendingProducts);
      setMuraTypeCounts(calculateMuraTypeCounts(data));
      setShowPreview(true);
      if (!pendingProducts.length) {
        Swal.fire('Info', 'No pending products awaiting approval.', 'info');
      }
    } catch (error) {
      console.error('Error fetching all products:', error);
      Swal.fire('Error!', error.message || 'Failed to fetch products', 'error');
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRelabelingProducts = async (email) => {
    setIsRelabelingLoading(true);
    try {
      const response = await getRelabelingRecordsByEmail(email);
      setRelabelingProducts(response.data || []);
      if (!response.data?.length) Swal.fire('Info', 'No relabeling products.', 'info');
      setShowRelabelingPreview(true);
    } catch (error) {
      console.error('Fetch relabeling error:', error);
      Swal.fire('Error!', 'Failed to fetch relabeling products', 'error');
      setRelabelingProducts([]);
    } finally {
      setIsRelabelingLoading(false);
    }
  };

  const handleUploadDetailsClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowUploadDetails(true);
  };

  // const handleRelabelingPreviewClick = async (e) => {
  //   e.stopPropagation();
  //   e.preventDefault();
  //   await fetchRelabelingProducts(email);
  // };

const handleRelabelingPreviewClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsRelabelingLoading(true);
    try {
      const response = await getRelabelingRecordsByEmail(email);
      const products = response.data || [];
      setRelabelingProducts(products);
      setRelabelingCount(products.length); // Update count
      if (!products.length) {
        Swal.fire('Info', 'No relabeling products.', 'info');
      }
      setShowRelabelingPreview(true);
    } catch (error) {
      console.error('Fetch relabeling error:', error);
      Swal.fire('Error!', 'Failed to fetch relabeling products', 'error');
      setRelabelingProducts([]);
      setRelabelingCount(0); // Reset count on error
    } finally {
      setIsRelabelingLoading(false);
    }
  };

  const handleClosePreview = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowPreview(false);
  };

  const handleCloseRelabelingPreview = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowRelabelingPreview(false);
    setRelabelingProducts(null);
  };




  const handleAcceptProduct = async (productId) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'You are about to accept this product record.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, accept it!',
      });
      if (result.isConfirmed) {
        await acceptMuraRecord(productId);
        Swal.fire('Accepted!', 'The product record has been accepted.', 'success');
        previewType === 'all' ? await fetchAllProducts() : await fetchMyProducts();
      }
    } catch (error) {
      console.error('Error accepting product:', error);
      Swal.fire('Error!', error.message || 'Failed to accept product record', 'error');
    }
  };

  const handleRejectProduct = async (productId) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'You are about to reject and permanently delete this issue record!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      });
      if (result.isConfirmed) {
        await rejectMuraRecord(productId);
        // Remove from UI
        setRelabelingProducts((prev) =>
          prev.filter((p) => p.originalProductId !== productId)
        );
        setRelabelingCount((prev) => Math.max(0, prev - 1));
        Swal.fire('Rejected!', 'The product record has been rejected and deleted.', 'success');
        //previewType === 'all' ? await fetchAllProducts() : await fetchMyProducts();
      }
    } catch (error) {
      console.error('Error rejecting product:', error);
      Swal.fire('Error!', error.message || 'Failed to reject product record', 'error');
    }
  };

  const handleRelabelProduct = async (productId) => {
    try {
      const result = await Swal.fire({
        title: 'Relabel Product',
        text: 'Mark this product for relabeling?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes',
      });
      if (result.isConfirmed) {
        await relabelMuraRecord(productId);
        Swal.fire('Success!', 'Product marked for relabeling.', 'success');
        await fetchAllProducts();
      }
    } catch (error) {
      console.error('Relabel error:', error);
      Swal.fire('Error!', error.message || 'Relabeling failed', 'error');
    }
  };

  const handleEditAndResubmit = async (product) => {
    try {
      const imageUrl = product.issueDetection?.originalImageUrl || product.MuraRecording?.rawImageUrl;
      if (!imageUrl || typeof imageUrl !== 'string') {
        throw new Error('Invalid or missing image URL');
      }
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }

      const blob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      await new Promise((resolve) => (reader.onloadend = resolve));
      const base64Image = reader.result;
      const img = new Image();
      img.src = base64Image;
      await new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image for dimensions'));
      });

      const imageDimensions = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };

      if (
        state.imageDimensions?.width !== img.naturalWidth ||
        state.imageDimensions?.height !== img.naturalHeight
      ) {
        updateState({ imageDimensions });
      }
      let muraData = {};
      try {
        muraData = JSON.parse(localStorage.getItem('muraData') || '{}');
        if (
          muraData.imageDimensions?.width !== img.naturalWidth ||
          muraData.imageDimensions?.height !== img.naturalHeight
        ) {
          muraData.imageDimensions = imageDimensions;
          localStorage.setItem('muraData', JSON.stringify(muraData));
        }
      } catch (e) {
        console.warn('Failed to parse muraData:', e);
        muraData.imageDimensions = imageDimensions;
        localStorage.setItem('muraData', JSON.stringify(muraData));
      }
      const defects = computeNormalizedDefects(product, imageDimensions);

      const recordedEntries = defects.map((defect, index) => {
        if (
          !defect.bbox ||
          !Array.isArray(defect.bbox) ||
          defect.bbox.length !== 4 ||
          defect.bbox.some((val) => !Number.isFinite(val))
        ) {
          console.warn(`Skipping invalid defect ${index} bbox:`, defect.bbox);
          return null;
        }

        // const muraType = defect.muraType
        //   ? defect.muraType.toLowerCase().replace(/\s+/g, '_')
        //   : 'other';
        const muraType = defect.muraType
          ? defect.muraType.toLowerCase().replace(/\s+/g, '_')
          : 'other';
          const color = MURA_TYPE_DATA?.[muraType]?.color 
  ?? MURA_TYPE_DATA?.other?.color 
  ?? '#ff0000';
  const muraTypeIndex = Number(
  MURA_TYPE_DATA?.[muraType]?.id 
  ?? MURA_TYPE_DATA?.other?.id 
  ?? 999)


        const rectangle = {
          x: (defect.bbox[0] - defect.bbox[2] / 2) * imageDimensions.width,
          y: (defect.bbox[1] - defect.bbox[3] / 2) * imageDimensions.height,
          width: defect.bbox[2] * imageDimensions.width,
          height: defect.bbox[3] * imageDimensions.height,
          muraType,
          //color: MURA_TYPE_DATA[muraType]?.color || MURA_TYPE_DATA['other'].color,
          //muraTypeIndex: Number(MURA_TYPE_DATA[muraType]?.id || MURA_TYPE_DATA['other'].id),
          color,
          muraTypeIndex,
          entryId: product._id,
        };

        return {
          muraType,
          rootCause: defect.muraLabelIssue?.muraRootCause || '',
          rootIssues: Array.isArray(defect.muraLabelIssue?.muraRootIssue)
            ? defect.muraLabelIssue.muraRootIssue
            : [],
          entryIndex: index,
          rectangles: [rectangle],
        };
      }).filter(entry => entry !== null);

      const rectangles = recordedEntries.flatMap(entry => entry.rectangles);
      const muraDataToStore = {
        rectangles,
        imageDimensions,
        muraType: recordedEntries[0]?.muraType || 'other',
      };
      localStorage.setItem('muraData', JSON.stringify(muraDataToStore));
      updateState({
        formData: [product.productInfo],
        uploadedImage: base64Image,
        editingProductId: product.originalProductId || product._id,
        imageDimensions,
        dataFromStatus: product.dataFromStatus,
      });

      const navigateState = {
        uploadedImage: base64Image,
        recordedEntries,
        issueType: defects.some(d => d.muraType === 'pressing_light_leakage')
          ? 'LightLeakage'
          : 'MuraLabeling',
        editingProductId: product.originalProductId || product._id,
        productInfo: product.productInfo,
        rectangles,
        muraData: muraDataToStore,
        yoloAnnotations: [],
        fromMuraLabeling: true,
        entryId: product.originalProductId || product._id,
        email: product.email,
        isResubmission: true,
        imageDimensions,
        dataFromStatus: product.dataFromStatus,
      };
      navigate('/muralabeling', { state: navigateState });
      setShowWarnings(false);
      setShowRelabelingPreview(false);
    } catch (error) {
      console.error('Error preparing relabel data:', error);
      Swal.fire('Error!', 'Failed to load product data for relabeling.', 'error');
    }
  };

  const handlePreviewClick = (product) => {

    const imageUrl = product.issueDetection?.originalImageUrl || product.MuraRecording?.rawImageUrl;

    if (!imageUrl || typeof imageUrl !== 'string') {
      console.error('Invalid image URL:', imageUrl);
      Swal.fire({
        title: 'Error!',
        text: 'Invalid image URL. Please try again or contact support.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    setPreviewImageUrl(imageUrl);
    setPreviewProduct(product);
    setShowImagePreview(true);
    console.log('Preview states set:', { showImagePreview: true, imageUrl, product });
  };

  const closeImagePreview = () => {
    console.log('Closing image preview');
    setShowImagePreview(false);
    setPreviewImageUrl(null);
    setPreviewProduct(null);
    setImageLoading(false);
  };

  const renderMuraRecordings = (defectInfos) => {
    if (!defectInfos || !Array.isArray(defectInfos) || defectInfos.length === 0) {
      return 'No issue recordings';
    }
    const filteredDefects = defectInfos.filter(defect => defect.muraType.toLowerCase() !== 'other');
    if (filteredDefects.length === 0) {
      return 'No issue recordings';
    }
    return filteredDefects
      .map((defect) => {
        const muraType = defect.muraType || 'unknown';
        const muraLabId = defect.muraLabId || 'unknown';
        const rootCause = defect.muraLabelIssue?.muraRootCause || '';
        const rootIssues = defect.muraLabelIssue?.muraRootIssue || [];
        const issuesString = rootIssues.length > 0 ? ` : ${rootIssues.join(', ')}` : '';
        return `ID: ${muraLabId} | ${muraType}${rootCause ? ` : ${rootCause}` : ''}${issuesString}`;
      })
      .join('\n') || 'No valid issue recordings';
  };

  const saveAndNavigate = () => {
    if (submissionBlocked || totalUploads >= maxUploads) {
      Swal.fire({
        title: 'Upload Limit Reached',
        text: 'The maximum upload count of 2000 has been filled. No further submissions are allowed.',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }
    if (!isFormValid()) {
      setError('Please fill all required fields');
      return;
    }
    const productInfo = {
      productType,
      brand,
      modelName,
      bezel: hasBezel ? 'yes' : 'no',
      leakageRatio,
      dimensions: {
        width: { whole: dimensions.width.whole, decimal: dimensions.width.decimal },
        height: { whole: dimensions.height.whole, decimal: dimensions.height.decimal },
      },
      screenType: productType === 'MNT' ? screenType : undefined,
      inchSize: selectedInch || undefined,
      companyInfo,
    };
    const updatedFormData = [...(state.formData || []), productInfo];
    updateState({ formData: updatedFormData });
    navigate('/upload-image', { state: { productInfo } });
  };

  const formatMuraType = (muraType) => {
    return muraType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (!isLoaded) return null;

  return (
    <div className="min-h-screen bg-black text-white p-4 overflow-x-hidden">
      <div className="flex items-center mb-5">
        <button className="mr-4" onClick={handleBack}>
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-medium">Product Info Input</h1>
        <div className="ml-auto flex space-x-2">
          <div className="relative group">
            <button
              className="flex items-center text-white py-0.5 px-2 rounded-lg disabled:bg-gray-600 bg-gray-700 hover:bg-gray-600 cursor-pointer"
              onClick={handleRelabelingPreviewClick}
              disabled={isRelabelingLoading}
              title="View Relabeling Products"
            >
              {isRelabelingLoading ? 'Loading...' : <Repeat className="w-5 h-5" />}
              {/* Badge Notification */}
              {relabelingCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {relabelingCount}
                </span>
              )}
            </button>
            <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-gray-700 text-white text-sm rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
              Relabeling Products
            </span>
          </div>
          <div className="relative group">
            <button
              className="flex items-center text-white py-0.5 px-2 rounded-lg disabled:bg-gray-600 bg-gray-700 hover:bg-gray-600 cursor-pointer"
              onClick={fetchMyProducts}
              disabled={isLoading}
              title="View My Accepted Products"
            >
              {isLoading && previewType === 'my' ? 'Loading...' : <Rows4 className="w-5 h-5" />}
            </button>
            <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-gray-700 text-white text-sm rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
              View My Accepted Products
            </span>
          </div>
          {/* {isAdmin && (
            <div className="relative group">
              <button
                className="flex items-center text-white py-0.5 px-2 rounded-lg disabled:bg-gray-600 bg-gray-700 hover:bg-gray-600 cursor-pointer"
                onClick={fetchAllProducts}
                disabled={isLoading}
                title="View Pending Products (Admin Only)"
              >
                {isLoading && previewType === 'all' ? 'Loading...' : <Users className="w-5 h-5" />}
              </button>
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-gray-700 text-white text-sm rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                View Pending Products (Admin Only)
              </span>
            </div>
          )} */}
        </div>
      </div>

      {showImagePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-60 overflow-x-hidden p-4">
          <div className="relative w-full h-full flex items-center justify-center overflow-x-hidden">
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
            <canvas
              ref={previewCanvasRef}
              className={`object-contain max-w-full max-h-full ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
            />
            <button
              onClick={closeImagePreview}
              className="absolute top-4 right-4 text-white bg-gray-800 rounded-full p-2 hover:bg-gray-700 transition-colors z-150"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {showUploadDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[70] p-4 overflow-x-hidden">
          <div className="bg-gray-800 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col overflow-x-hidden">
            <div className="bg-gray-900 px-6 py-4 flex justify-between items-center border-b border-gray-700">
              <h2 className="text-2xl font-semibold text-white">Issue Details</h2>
              <button
                onClick={() => setShowUploadDetails(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="px-6 py-4 border-b border-gray-700">
              <div className="grid grid-cols-2 gap-4 text-gray-200">
                <div>
                  <div>Max Uploads: {maxUploads}</div>
                  <input
                    type="range"
                    min="0"
                    max={maxUploads}
                    value={totalUploads}
                    disabled
                    style={{ background: sliderBg }}
                    className="mt-2 w-full h-2 bg-gray-200 rounded-full appearance-none opacity-70 transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-default [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-default"
                  />
                </div>
                <div>Total Uploaded Issues: {totalUploads}</div>
                {previewType === 'my' ? (
                  <>
                    <div>My Uploaded Issues: {myUploads}</div>
                    <div>My Accepted Issues: {myAccepted}</div>
                    <div>Total Accepted Issues: {totalAccepted}</div>
                    <div className="col-span-2">
                      <div className="font-semibold bg-gray-700 px-2 py-1 rounded">Issue Type Counts</div>
                      <div className="mt-2 bg-black p-4 rounded-lg w-full overflow-x-hidden">
                        {Object.keys(muraTypeCounts).length === 0 ? (
                          <div className="text-gray-400">No issue types available. Please check your uploads.</div>
                        ) : (
                          Object.entries(muraTypeCounts).map(([muraType, { count, ids }]) => (
                            <div key={muraType} className="mb-4">
                              <div className="flex justify-between">
                                <span>{formatMuraType(muraType)}:</span>
                                <span>{count}</span>
                              </div>
                              <div className="text-sm text-gray-400 mt-1">
                                IDs: {ids.length > 0 ? ids.join(', ') : 'None'}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>Total Accepted Issues: {totalAccepted}</div>
                    <div>Total Pending Issues: {totalPending}</div>
                    <div className="col-span-2">
                      <div className="font-semibold bg-gray-700 px-2 py-1 rounded">Issue Type Counts</div>
                      <div className="mt-2 bg-black p-4 rounded-lg w-full overflow-x-hidden">
                        {Object.keys(muraTypeCounts).length === 0 ? (
                          <div className="text-gray-400">No issue types available. Please check your uploads.</div>
                        ) : (
                          Object.entries(muraTypeCounts).map(([muraType, { count, ids }]) => (
                            <div key={muraType} className="mb-4">
                              <div className="flex justify-between">
                                <span>{formatMuraType(muraType)}:</span>
                                <span>{count}</span>
                              </div>
                              <div className="text-sm text-gray-400 mt-1">
                                IDs: {ids.length > 0 ? ids.join(', ') : 'None'}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              {submissionBlocked && (
                <div className="mt-4 text-red-500">
                  Maximum upload limit of 2000 reached. No further submissions allowed.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showRelabelingPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4 overflow-x-hidden">
          <div
            className="bg-gray-800 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col overflow-x-hidden"
            onClick={handleCloseRelabelingPreview}
          >
            <div className="bg-gray-900 px-6 py-4 flex justify-between items-center border-b border-gray-700">
              <h2 className="text-2xl font-semibold text-white">Relabeling Products</h2>
              <button
                onClick={handleCloseRelabelingPreview}
                className="text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overflow-x-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 pb-12">
                {isRelabelingLoading ? (
                  <div className="text-center py-10 text-gray-400">Loading relabeling products...</div>
                ) : relabelingProducts.length === 0 ? (
                  <div className="text-center py-10 text-gray-400">No products marked for relabeling</div>
                ) : (
                  <div className="grid gap-6">
                    {relabelingProducts.map((product) => (
                      <div
                        key={product._id}
                        className="bg-gray-900 rounded-lg p-5 hover:shadow-lg transition-colors duration-200 border border-gray-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <h3 className="text-xl font-medium text-blue-400 border-b border-gray-700 pb-2">
                              Product Information
                            </h3>
                            <div className="space-y-2 text-gray-200">
                              <div className="flex justify-between">
                                <span className="text-gray-400">Type:</span>
                                <span>{product.productInfo.productType}</span>
                              </div>
                               {product.productInfo?.brand && (
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Brand:</span>
                                  <span>{product.productInfo.brand}</span>
                                </div>
                              )} 

                              <div className="flex justify-between">
                                <span className="text-gray-400">Model:</span>
                                <span>{product.productInfo.modelName}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Bezel:</span>
                                <span>{product.productInfo.bezel}</span>
                              </div>
                              {product.productInfo.screenType && (
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Screen Type:</span>
                                  <span>{product.productInfo.screenType}</span>
                                </div>
                              )}
                              {product.productInfo.inchSize && (
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Size:</span>
                                  <span>{product.productInfo.inchSize}"</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-gray-400">Dimensions:</span>
                                <span>
                                  {`${product.productInfo.dimensions?.width?.whole || 0}.${product.productInfo.dimensions?.width?.decimal || '0'} × ${product.productInfo.dimensions?.height?.whole || 0}.${product.productInfo.dimensions?.height?.decimal || '0'} mm`}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Company:</span>
                                <span>{product.productInfo.companyInfo || 'Unknown'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Status:</span>
                                <span className="text-yellow-400">Relabeling</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">dataFromStatus:</span>
                                <span>{product.dataFromStatus}</span>
                              </div>
                                <div className="flex justify-between">
                                <span className="text-gray-400">Comment:</span>
                                <span>{product.Comment}</span>
                              </div>
                              <div className="space-y-1">
                                <span className="text-gray-400">Issue Recordings:</span>
                                <pre className="bg-gray-800 p-3 rounded text-sm text-gray-200 whitespace-pre-wrap overflow-x-hidden">
                                  {renderMuraRecordings(getDefectInfo(product))}
                                </pre>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <h3 className="text-xl font-medium text-blue-400 border-b border-gray-700 pb-2">
                              Defect Image
                            </h3>
                            <div className="space-y-2">
                              <button
                                onClick={() => handlePreviewClick(product)}
                                className="flex items-center justify-center w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                <Eye className="w-5 h-5 mr-2" /> Preview
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 flex justify-end space-x-4 pb-2">
                        <button
                            onClick={() => handleRejectProduct(product.originalProductId)}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                          >
                            Delete
                          </button>
                          <button
                            onClick={() => handleEditAndResubmit(product)}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            Edit and Resubmit
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showPreview && products && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4 overflow-x-hidden">
          <div
            className="bg-gray-800 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col overflow-x-hidden"
            onClick={handleClosePreview}
          >
            <div className="bg-gray-900 px-6 py-4 flex justify-between items-center border-b border-gray-700">
              <h2 className="text-2xl font-semibold text-white">
                {previewType === 'all' ? 'Pending Products (Admin View)' : 'My Accepted Products'}
              </h2>
              <div className="flex items-center space-x-2">
                <div className="relative group">
                  <OctagonAlert
                    className="w-7 h-7 font-semibold text-white cursor-pointer"
                    onClick={handleUploadDetailsClick}
                  />
                  <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-gray-700 text-white text-sm rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                    Issue Details
                  </span>
                </div>
                <button
                  onClick={handleClosePreview}
                  className="text-gray-400 hover:text-white transition-colors p-2 rounded-full hover:bg-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto overflow-x-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 pb-12">
                {products.length === 0 ? (
                  <div className="text-center py-10 text-sm text-gray-400">
                    {previewType === 'all' ? 'No products awaiting approval' : 'No accepted products found'}
                  </div>
                ) : (
                  <div className="grid gap-6">
                    {products.map((product) => (
                      <div
                        key={product._id}
                        className="bg-gray-900 rounded-lg p-5 hover:shadow-lg transition-colors duration-200 border border-gray-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <h3 className="text-xl font-medium text-blue-400 border-b border-gray-700 pb-2">
                              Product Information
                            </h3>
                            <div className="space-y-2 text-gray-200">
                              <div className="flex justify-between">
                                <span className="text-gray-400">Type:</span>
                                <span>{product.productInfo.productType}</span>
                              </div>
                               <div className="flex justify-between">
                                <span className="text-gray-400">Brand:</span>
                                <span>{product.productInfo.brand}</span>
                              </div> 
                              <div className="flex justify-between">
                                <span className="text-gray-400">Model:</span>
                                <span>{product.productInfo.modelName}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Bezel:</span>
                                <span>{product.productInfo.bezel}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Dimensions:</span>
                                <span>
                                  {`${product.productInfo.dimensions?.width?.whole || 0}.${product.productInfo.dimensions?.width?.decimal || '0'} × ${product.productInfo.dimensions?.height?.whole || 0}.${product.productInfo.dimensions?.height?.decimal || '0'} mm`}
                                </span>
                              </div>
                              {product.productInfo.inchSize && (
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Size:</span>
                                  <span>{product.productInfo.inchSize}"</span>
                                </div>
                              )}
                              {product.productInfo.screenType && (
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Screen Type:</span>
                                  <span>{product.productInfo.screenType}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-gray-400">Company:</span>
                                <span>{product.productInfo.companyInfo || 'Unknown'}</span>
                              </div>
                              {isAdmin && previewType === 'all' && (
                                <div className="flex justify-between">
                                  <span className="text-gray-400">By:</span>
                                  <span>{product.email}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-gray-400">Status:</span>
                                <span
                                  className={
                                    product.status === 'accepted'
                                      ? 'text-green-500'
                                      : product.status === 'rejected'
                                        ? 'text-red-400'
                                        : product.status === 'relabeling'
                                          ? 'text-yellow-400'
                                          : 'text-yellow-400'
                                  }
                                >
                                  {product.status === null
                                    ? 'Pending'
                                    : product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">dataFromStatus:</span>
                                <span>{product.dataFromStatus}</span>
                              </div>
                              <div className="space-y-1">
                                <span className="text-gray-400">Issue Recordings:</span>
                                <pre className="bg-gray-800 p-3 rounded text-sm text-gray-200 whitespace-pre-wrap overflow-x-hidden">
                                  {renderMuraRecordings(getDefectInfo(product))}
                                </pre>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-4">
                            <h3 className="text-xl font-medium text-blue-400 border-b border-gray-700 pb-2">
                              Defect Image
                            </h3>
                            <div className="space-y-2">
                              <button
                                onClick={() => handlePreviewClick(product)}
                                className="flex items-center justify-center w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                <Eye className="w-5 h-5 mr-2" /> Preview
                              </button>
                            </div>
                          </div>
                        </div>
                        {isAdmin && (product.status === null || product.status === 'pending') && (
                          <div className="mt-6 flex justify-end space-x-4 pb-2">
                            <button
                              onClick={() => handleRelabelProduct(product._id)}
                              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                            >
                              Relabel
                            </button>
                            <button
                              onClick={() => handleAcceptProduct(product._id)}
                              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleRejectProduct(product._id)}
                              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6 p-2 overflow-x-hidden">
        <div>
          <label className="block mb-3">Product Type</label>
          <div className="relative">
            <div className="flex w-full">
              {['TV', 'MNT', 'PD'].map((type) => (
                <button
                  key={type}
                  onClick={() => setProductType(type)}
                  className={`flex-1 py-2 text-center relative ${productType === type ? 'text-blue-500' : 'text-gray-400'
                    }`}
                >
                  {type}
                  {productType === type && (
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500"></div>
                  )}
                </button>
              ))}
            </div>
            <div className="absolute bottom-0 left-0 w-full h-px bg-gray-700"></div>
          </div>
        </div>
         <div>
          <label className="block mb-3">Brand</label>
          <input
            type="text"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="Enter brand name"
            className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 placeholder-gray-400"
          />
        </div> 
        <div>
          <label className="block mb-3">Model Name</label>
          <input
            type="text"
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            placeholder="Enter model name"
            className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 placeholder-gray-400"
          />
        </div>
        {productType !== 'PD' && (
          <div>
            <label className="block mb-3">Bezel</label>
            <div className="flex space-x-4">
              <button
                onClick={() => setHasBezel(true)}
                className={`px-6 py-2 rounded-full ${hasBezel === true ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                  }`}
              >
                Yes
              </button>
              <button
                onClick={() => setHasBezel(false)}
                className={`px-6 py-2 rounded-full ${hasBezel === false ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                  }`}
              >
                No
              </button>
            </div>
          </div>
        )}

        {(productType === 'TV' || productType === 'MNT') && (
          <div>
            {productType === 'MNT' && (
              <div className="mb-3">
                <label className="block mb-1">Screen Type</label>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setScreenType(screenType === 'Flat' ? null : 'Flat')}
                    className={`px-6 py-2 rounded-full ${screenType === 'Flat' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                      }`}
                  >
                    Flat
                  </button>
                  <button
                    onClick={() => setScreenType(screenType === 'Curved' ? null : 'Curved')}
                    className={`px-6 py-2 rounded-full ${screenType === 'Curved' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                      }`}
                  >
                    Curved
                  </button>
                </div>
              </div>
            )}
            <div className="mb-3">
              <label className="block mb-2">Inch Size Selection</label>
              <div className="relative">
                <select
                  className="dropdown block w-full bg-gray-800 text-white rounded-lg px-4 py-3"
                  onChange={(e) => {
                    const selectedInch = e.target.value;
                    const inchToDimensions =
                      productType === 'TV'
                        ? {
                          '32': { width: '708', height: '398' },
                          '40': { width: '885', height: '498' },
                          '42': { width: '933', height: '535' },
                          '43': { width: '952', height: '535' },
                          '48': { width: '1069', height: '612' },
                          '50': { width: '1107', height: '623' },
                          '55': { width: '1218', height: '685' },
                          '58': { width: '1285', height: '723' },
                          '65': { width: '1439', height: '810' },
                          '70': { width: '1721', height: '985' },
                          '75': { width: '1660', height: '934' },
                          '77': { width: '1562', height: '860' },
                          '85': { width: '1882', height: '1058' },
                          '86': { width: '1905', height: '1072' },
                          '98': { width: '2170', height: '1220' },
                          '100': { width: '2214', height: '1246' },
                        }
                        : {
                          '16': screenType === 'Flat' ? { width: '364', height: '230' } : { width: '', height: '' },
                          '22': screenType === 'Flat' ? { width: '492', height: '290' } : { width: '', height: '' },
                          '24': screenType === 'Flat' ? { width: '543', height: '320' } : { width: '537', height: '323' },
                          '25': screenType === 'Flat' ? { width: '560', height: '324' } : { width: '', height: '' },
                          '27': screenType === 'Flat' ? { width: '614', height: '368' } : { width: '612', height: '368' },
                          '30': screenType === 'Flat' ? { width: '', height: '' } : { width: '701', height: '322' },
                          '32': screenType === 'Flat' ? { width: '715', height: '421' } : { width: '700', height: '424' },
                          '34': screenType === 'Flat' ? { width: '817', height: '371' } : { width: '810', height: '366' },
                          '45': screenType === 'Flat' ? { width: '', height: '' } : { width: '1085', height: '337' },
                          '49': screenType === 'Flat' ? { width: '', height: '' } : { width: '1194', height: '370' },
                        };
                    if (selectedInch) {
                      const { width, height } = inchToDimensions[selectedInch] || { width: '', height: '' };
                      setDimensions({
                        width: {
                          input: width,
                          whole: width ? parseInt(width) : 0,
                          decimal: width && width.includes('.') ? width.split('.')[1][0] || '0' : '0',
                        },
                        height: {
                          input: height,
                          whole: height ? parseInt(height) : 0,
                          decimal: height && height.includes('.') ? height.split('.')[1][0] || '0' : '0',
                        },
                      });
                    } else {
                      setDimensions({
                        width: { input: '', whole: 0, decimal: '0' },
                        height: { input: '', whole: 0, decimal: '0' },
                      });
                    }
                    setSelectedInch(selectedInch);
                  }}
                  value={selectedInch}
                >
                  <option value="">Select inch size</option>
                  {productType === 'TV' ? (
                    <>
                      <option value="32">32 inches</option>
                      <option value="40">40 inches</option>
                      <option value="42">42 inches</option>
                      <option value="43">43 inches</option>
                      <option value="48">48 inches</option>
                      <option value="50">50 inches</option>
                      <option value="55">55 inches</option>
                      <option value="58">58 inches</option>
                      <option value="65">65 inches</option>
                      <option value="70">70 inches</option>
                      <option value="75">75 inches</option>
                      <option value="77">77 inches</option>
                      <option value="85">85 inches</option>
                      <option value="86">86 inches</option>
                      <option value="98">98 inches</option>
                      <option value="100">100 inches</option>
                    </>
                  ) : (
                    <>
                      <option value="16">16 inches</option>
                      <option value="22">22 inches</option>
                      <option value="24">24 inches</option>
                      <option value="25">25 inches</option>
                      <option value="27">27 inches</option>
                      <option value="30">30 inches</option>
                      <option value="32">32 inches</option>
                      <option value="34">34 inches</option>
                      <option value="45">45 inches</option>
                      <option value="49">49 inches</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
        )}
        {['width', 'height'].map((dimension) => (
          <div key={dimension}>
            <label className="block mb-3 capitalize">
              {dimension === 'width' ? 'Width: mm' : 'Height: mm'}
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="text"
                value={dimensions[dimension].input}
                onChange={(e) => handleDimensionChange(dimension, e.target.value)}
                placeholder="Manually input"
                className="w-35 bg-gray-800 text-white rounded-lg px-4 py-3 placeholder-gray-400"
              />
            </div>
          </div>
        ))}
        {error && <div className="text-red-500 text-sm">{error}</div>}
        <button
          onClick={saveAndNavigate}
          disabled={!isFormValid() || submissionBlocked}
          className={`w-full py-3 rounded-lg mt-6 ${isFormValid() && !submissionBlocked
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default ProductInfoInput;